import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, MoreHorizontal, Search, X } from 'lucide-react';
import { type DailyAnalyticsDashboardData } from '../../../services/MasterAdmin/dailyAnalyticsDashboardService';
import { opsTableWrap } from '../operations/operationsUi';

type ContentKind = 'blogs' | 'awareness';

type ContentRow = {
  id: string;
  title: string;
  meta: string;
  totalReads: number;
  uniqueReaders: number;
  avgDwell: number;
};

type ReaderRow = {
  key: string;
  name: string;
  times: number;
  dwellSec: number;
  lastRead?: string;
};

type ContentStats = {
  reads: number;
  users: Set<string>;
  dwell: number;
  dwellN: number;
};

function formatDuration(sec?: number): string {
  const value = Number(sec || 0);
  if (!value || Number.isNaN(value)) return '—';
  if (value < 60) return `${Math.round(value)}s`;
  const min = Math.floor(value / 60);
  const rem = Math.round(value % 60);
  return rem ? `${min}m ${rem}s` : `${min}m`;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function displayName(name?: string): string {
  const trimmed = String(name || '').trim();
  if (!trimmed || trimmed.toLowerCase() === 'n/a' || trimmed.toLowerCase() === 'anonymous') return 'Guest reader';
  return trimmed;
}

function toContentRows(
  items: Array<{ id: string; title: string; meta: string }>,
  statsMap: Record<string, ContentStats>,
): ContentRow[] {
  return items
    .map((item) => {
      const stats = statsMap[item.id];
      const dwellN = stats ? stats.dwellN : 0;
      return {
        id: item.id,
        title: item.title,
        meta: item.meta,
        totalReads: stats ? stats.reads : 0,
        uniqueReaders: stats ? stats.users.size : 0,
        avgDwell: dwellN > 0 && stats ? stats.dwell / dwellN : 0,
      };
    })
    .sort((a, b) => b.totalReads - a.totalReads);
}

const ContentEngagementPanel: React.FC<{
  data: DailyAnalyticsDashboardData | null;
  loading: boolean;
}> = ({ data, loading }) => {
  const [kind, setKind] = useState<ContentKind>('blogs');
  const [query, setQuery] = useState('');
  const [menuId, setMenuId] = useState<string | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);

  useEffect(() => {
    if (!menuId) return;
    const close = () => setMenuId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuId]);

  const blogs = useMemo(() => {
    const statsMap: Record<string, ContentStats> = {};
    const items: Array<{ id: string; title: string; meta: string }> = (data?.allBlogs || []).map((blog) => ({
      id: String(blog.id),
      title: blog.title,
      meta: blog.category_name || '—',
    }));

    (data?.readers || []).forEach((row) => {
      const key = String(row.blog_id);
      if (!statsMap[key]) statsMap[key] = { reads: 0, users: new Set<string>(), dwell: 0, dwellN: 0 };
      statsMap[key].reads += Number(row.read_count || 0);
      statsMap[key].users.add(String(row.user_id));
      if (Number(row.avg_dwell_sec || 0) > 0) {
        statsMap[key].dwell += Number(row.avg_dwell_sec);
        statsMap[key].dwellN += 1;
      }
      if (!items.some((item) => item.id === key)) {
        items.push({
          id: key,
          title: row.blog_title || `Blog ${key}`,
          meta: row.blog_category || '—',
        });
      }
    });

    return toContentRows(items, statsMap);
  }, [data]);

  const awarenessItems = useMemo(() => {
    const statsMap: Record<string, ContentStats> = {};
    const items: Array<{ id: string; title: string; meta: string }> = (data?.allAwareness || []).map((item) => ({
      id: String(item.id),
      title: item.title,
      meta: item.event_type || '—',
    }));

    (data?.awareness || []).forEach((row) => {
      const key = String(row.event_id);
      if (!statsMap[key]) statsMap[key] = { reads: 0, users: new Set<string>(), dwell: 0, dwellN: 0 };
      statsMap[key].reads += Number(row.view_count || 0);
      statsMap[key].users.add(String(row.user_id));
      if (Number(row.avg_dwell_sec || 0) > 0) {
        statsMap[key].dwell += Number(row.avg_dwell_sec);
        statsMap[key].dwellN += 1;
      }
      if (!items.some((item) => item.id === key)) {
        items.push({
          id: key,
          title: row.event_title || `Health card ${key}`,
          meta: 'health',
        });
      }
    });

    return toContentRows(items, statsMap);
  }, [data]);

  const rows: ContentRow[] = kind === 'blogs' ? blogs : awarenessItems;
  const filtered: ContentRow[] = query.trim()
    ? rows.filter((row) => {
        const q = query.toLowerCase();
        return row.title.toLowerCase().includes(q) || row.meta.toLowerCase().includes(q);
      })
    : rows;

  const selected = rows.find((row) => row.id === String(analysisId)) || null;

  const selectedReaders = useMemo((): ReaderRow[] => {
    if (!analysisId) return [];
    if (kind === 'blogs') {
      return (data?.readers || [])
        .filter((row) => String(row.blog_id) === String(analysisId))
        .map((row) => ({
          key: String(row.user_id),
          name: displayName(row.user_name),
          times: Number(row.read_count || 0),
          dwellSec: Number(row.avg_dwell_sec || row.total_dwell_sec || 0),
          lastRead: row.last_read_time,
        }))
        .sort((a, b) => b.times - a.times);
    }
    return (data?.awareness || [])
      .filter((row) => String(row.event_id) === String(analysisId))
      .map((row) => ({
        key: String(row.user_id),
        name: displayName(row.user_name),
        times: Number(row.view_count || 0),
        dwellSec: Number(row.avg_dwell_sec || row.total_dwell_sec || 0),
        lastRead: row.last_view_time,
      }))
      .sort((a, b) => b.times - a.times);
  }, [analysisId, data, kind]);

  const totalViews = selectedReaders.reduce((sum, row) => sum + row.times, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-900/60">
          <button
            type="button"
            onClick={() => { setKind('blogs'); setAnalysisId(null); setMenuId(null); }}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold ${kind === 'blogs' ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-800' : 'text-gray-500'}`}
          >
            Health blogs
          </button>
          <button
            type="button"
            onClick={() => { setKind('awareness'); setAnalysisId(null); setMenuId(null); }}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold ${kind === 'awareness' ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-800' : 'text-gray-500'}`}
          >
            Health awareness
          </button>
        </div>
        <label className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={kind === 'blogs' ? 'Search blogs…' : 'Search awareness cards…'}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </label>
      </div>

      <div className={opsTableWrap}>
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-gray-100 dark:bg-gray-700/50" />
            ))}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:border-gray-700">
                <th className="px-4 py-3">{kind === 'blogs' ? 'Blog' : 'Awareness card'}</th>
                <th className="px-4 py-3">{kind === 'blogs' ? 'Category' : 'Type'}</th>
                <th className="px-4 py-3 text-right">Views</th>
                <th className="px-4 py-3 text-right">Readers</th>
                <th className="px-4 py-3 text-right">Avg. time</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    No {kind === 'blogs' ? 'blogs' : 'awareness cards'} in this period.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50 dark:border-gray-700/40">
                    <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">{row.title}</td>
                    <td className="px-4 py-3 text-xs uppercase text-gray-500">{row.meta}</td>
                    <td className="px-4 py-3 text-right font-bold tabular-nums">{row.totalReads.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.uniqueReaders.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatDuration(row.avgDwell)}</td>
                    <td className="relative px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuId((prev) => (prev === row.id ? null : row.id));
                        }}
                        className="inline-flex rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                        aria-label="Actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                      {menuId === row.id && (
                        <div className="absolute right-4 z-20 mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 text-left shadow-lg dark:border-gray-700 dark:bg-gray-800">
                          <button
                            type="button"
                            onClick={() => { setAnalysisId(row.id); setMenuId(null); }}
                            className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            Analysis
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAnalysisId(null)}>
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
              <div className="min-w-0 pr-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {kind === 'blogs' ? 'Blog analysis' : 'Awareness analysis'}
                </p>
                <h4 className="mt-1 text-base font-semibold text-gray-900 dark:text-white">{selected.title}</h4>
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Views</p>
                    <p className="font-bold">{selected.totalReads.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Readers</p>
                    <p className="font-bold">{selected.uniqueReaders.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Avg. time</p>
                    <p className="font-bold">{formatDuration(selected.avgDwell)}</p>
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => setAnalysisId(null)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white dark:bg-gray-800">
                  <tr className="border-b border-gray-100 text-left text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:border-gray-700">
                    <th className="px-5 py-3">Reader</th>
                    <th className="px-5 py-3 text-right">Times read</th>
                    <th className="px-5 py-3 text-right">Time spent</th>
                    <th className="px-5 py-3 text-right">Last read</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedReaders.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-5 py-10 text-center text-gray-400">No readers yet.</td>
                    </tr>
                  ) : (
                    selectedReaders.map((row) => (
                      <tr key={row.key} className="border-b border-gray-50 dark:border-gray-700/40">
                        <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">{row.name}</td>
                        <td className="px-5 py-3 text-right">{row.times} {row.times === 1 ? 'time' : 'times'}</td>
                        <td className="px-5 py-3 text-right">{formatDuration(row.dwellSec)}</td>
                        <td className="px-5 py-3 text-right text-gray-500">{formatDate(row.lastRead)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-gray-100 px-5 py-3 text-xs text-gray-400 dark:border-gray-700">
              Showing names only · {totalViews.toLocaleString()} total views in this period
            </div>
          </div>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <BookOpen className="mb-2 h-8 w-8 opacity-40" />
          <p className="text-sm">No published {kind === 'blogs' ? 'blogs' : 'awareness cards'} found.</p>
        </div>
      )}
    </div>
  );
};

export default ContentEngagementPanel;
